import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";  

interface UpdateCaseResponse {
  success: boolean;
  message: string;
  data: string;
}

interface StatusState {
  statusUpdate: string | null;
  success: boolean | null;
  message: string | null;
  isUpdated: boolean | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: StatusState = {
  statusUpdate: null,
  success: null,
  message: null,
  isUpdated: null,
  isLoading: false,
  error: null,
};

export const updateCaseStatus = createAsyncThunk<
  UpdateCaseResponse,
  { case_id: string; updatedStatus: string },
  { rejectValue: string }
>(
  "admin/updateCaseStatus",
  async ({ case_id, updatedStatus }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/auth/admin/cases/${case_id}/status/${updatedStatus}`
      );
      return response.data as UpdateCaseResponse;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update status"
      );
    }
  }
);

export const updateLiveCaseToPendingLive = createAsyncThunk<
  UpdateCaseResponse,
  { case_id: string },
  { rejectValue: string }
>(
  "admin/updateLiveCaseToPendingLive",
  async ({ case_id }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/auth/admin/pending-live`,
        { case_id }
      );
      return response.data as UpdateCaseResponse;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update to pending live"
      );
    }
  }
);

const caseSlice = createSlice({
  name: "adminCases",
  initialState,
  reducers: {
    clearStatusUpdate: (state) => {
      state.statusUpdate = null;
      state.success = null;
      state.message = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateCaseStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCaseStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statusUpdate = action.payload.data;
        state.success = action.payload.success;
        state.message = action.payload.message;
      })
      .addCase(updateCaseStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(updateLiveCaseToPendingLive.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isUpdated = null;
      })

      .addCase(updateLiveCaseToPendingLive.fulfilled, (state) => {
        state.isLoading = false;
        state.isUpdated = true;
      })

      .addCase(updateLiveCaseToPendingLive.rejected, (state, action) => {
        state.isLoading = false;
        state.isUpdated = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearStatusUpdate } = caseSlice.actions;
export default caseSlice.reducer;
